import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, Route, Switch, useHistory } from 'react-router-dom';
import { useQuery } from 'react-query';
import * as Sentry from '@sentry/react';

import urls, { urlPrefixWithoutWorkspace } from '@constants/urls';
import withPageWrapper from '@hocs/withPageWrapper';
import { checkLoggedIn } from '@utils/loginDetector';
import { checkIsAnyNotLoaded } from '@utils/loadingChecks';

import useAppUpdates from '@hooks/useAppUpdates';
import useIntercom from '@hooks/useIntercom';
import useCurrentWorkspaceId from '@hooks/useCurrentWorkspaceId';

import NotificationsSidebar from '@components/NotificationsSidebar/NotificationsSidebar';
import WorkspacesSidebar from '@components/WorkspacesSidebar/WorkspacesSidebar';
import Loader from '@uikit/old/Loader/Loader';
import NoAccessMessage from '@uikit/old/NoAccessMessage/NoAccessMessage';
import BackdoorLoginPage from '@pages/BackdoorLoginPage/BackdoorLoginPage';
import LoginPage from '@pages/LoginPage/LoginPage';
import MaintenancePage from '@pages/MaintenancePage/MaintenancePage';
import ExternalUnsubscribePage from '@pages/ExternalUnsubscribePage/ExternalUnsubscribePage';
import UnsubscribeConfirmationPage from '@pages/UnsubscribeConfirmationPage/UnsubscribeConfirmationPage';
import OrdersPage from '@components/Publisher/Orders/OrdersPage/OrdersPage';
import WebsitePages from '@pages/Publisher/WebsitePages/WebsitePages';
import Websites from '@components/Publisher/Websites/Websites/Websites';
import SettingsPages from '@pages/Publisher/SettingsPages/SettingsPages';

import { DispatchType } from 'src/store';
import initSentry from '@helpers/initSentry';

import { fetchUserProfile } from '@redux/thunks/userProfileRequests';
import { workspacesSelector } from '@redux/selectors/workspaces.selectors';
import { isStandaloneMode } from '@constants/app-modes';
import queryCacheKeysConstants from '@constants/queryCacheKeys.constants';
import PublisherOnboardingPage from '@pages/Publisher/OnboardingPage/PublisherOnboardingPage';

const SentryRoute = Sentry.withSentryRouting(Route);

export default (): JSX.Element => {
  const dispatch = useDispatch<DispatchType>();
  const history = useHistory();
  const [loginStatus, setLoginStatus] = useState<boolean | null>(null);
  const { workspaces, loadingStatus } = useSelector(workspacesSelector);
  const [isNoAccess, setIsNoAccess] = useState<boolean>(false);
  const workspaceId = useCurrentWorkspaceId();

  useEffect(() => {
    initSentry(history);
  }, []);

  useEffect(() => {
    const params = history.location.pathname.split('/');
    const startOfUrl = params[1];

    if (workspaces?.length === 0) return;

    if (urlPrefixWithoutWorkspace.includes(startOfUrl)) {
      setIsNoAccess(false);
      return;
    }

    if (checkIsAnyNotLoaded(loadingStatus)) {
      setIsNoAccess(false);
      return;
    }

    if (startOfUrl !== 'workspaces') {
      setIsNoAccess(false);
      return;
    }

    const workspaceIdFromUrl = parseInt(params[2], 10);
    if (isNaN(workspaceIdFromUrl)) return;

    const hasAccess = workspaces.some((workspace) => workspace.id === workspaceIdFromUrl);
    if (!hasAccess) {
      console.error(
        "Can't find workspace with id: ",
        workspaceIdFromUrl,
        ' in workspaces: ',
        workspaces.map((workspace) => workspace.id).join(', '),
        ''
      );
    }
    setIsNoAccess(!hasAccess);
  }, [history.location.pathname, loadingStatus, workspaces, workspaceId]);

  const { data: userProfile } = useQuery(
    queryCacheKeysConstants.userProfile,
    () => fetchUserProfile(dispatch),
    {
      enabled: false,
      retry: false,
      staleTime: 2 * (60 * 1000),
    }
  );

  const checkIsLoggedIn = useCallback(async () => {
    checkLoggedIn(history)
      .then((status) => {
        setLoginStatus(status);
      })
      .catch((error) => {
        console.error('Error while checking login status', error);
        setLoginStatus(false);
      });
  }, [dispatch]);

  if (isStandaloneMode()) {
    useIntercom();
    useAppUpdates();
  }

  useEffect(() => {
    if (userProfile == null && loginStatus === true) {
      setLoginStatus(null);
    }
    checkIsLoggedIn();
  }, [checkIsLoggedIn, userProfile]);

  return useMemo(() => {
    if (loginStatus === null) {
      return (
        <div>
          <Loader isLoading />
        </div>
      );
    }

    if (loginStatus === false) {
      return (
        <Switch>
          <SentryRoute path={urls.UNSUBSCRIBE} component={ExternalUnsubscribePage} />
          <SentryRoute path={urls.UNSUBSCRIBED} component={UnsubscribeConfirmationPage} />
          <SentryRoute path={urls.MAINTENANCE} component={MaintenancePage} />

          <SentryRoute path={urls.BD_LOGIN} component={BackdoorLoginPage} />

          <SentryRoute path={urls.AUTHORIZE_MICROSOFT_ERROR} component={MaintenancePage} />

          <SentryRoute path="/authorize/login?:token" component={LoginPage} />

          <SentryRoute path="*" component={LoginPage} />

          <SentryRoute path="/">
            <Redirect to={urls.LOGIN} />
          </SentryRoute>
        </Switch>
      );
    }

    if (isNoAccess) {
      return <NoAccessMessage showButton />;
    }

    return (
      <>
        <Switch>
          <SentryRoute path={urls.MAINTENANCE} component={MaintenancePage} />
          <SentryRoute path="/onboarding/:onboardingPage?" component={PublisherOnboardingPage} />

          <SentryRoute exact path={urls.ORDERS()} component={withPageWrapper(OrdersPage)} />
          <SentryRoute exact path={urls.WEBSITES()} component={withPageWrapper(Websites)} />
          <SentryRoute
            path={urls.WEBSITE_PAGES()}
            component={withPageWrapper(WebsitePages, false)}
          />
          <SentryRoute path={urls.SETTINGS} component={withPageWrapper(SettingsPages)} />

          <SentryRoute path="*">
            {(() => {
              return <Redirect to={urls.ORDERS(workspaceId)} />;
            })()}
          </SentryRoute>
        </Switch>
        <WorkspacesSidebar />
        <NotificationsSidebar />
      </>
    );
  }, [isNoAccess, loginStatus, workspaceId]);
};
